import { TypedDocumentNode, gql } from '@apollo/client';
import { faCheckCircle, faGift } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { ReactNode, useState } from 'react';
import { flushSync } from 'react-dom';
import { FormattedMessage } from 'react-intl';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import {
  So5FixtureEvent,
  So5LeaderboardType,
} from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Dropdown } from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { ANY_SPORT_PLAY_EVENTTYPE_FIXTURE } from '@sorare/core/src/constants/routes';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import { glossary } from '@sorare/core/src/lib/glossary';
import { generateSportPath } from '@sorare/core/src/lib/routing/generateSportPath';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';
import { regularMaterial } from '@sorare/core/src/style/utils';

import { hasGamesStarted, isFixtureOpened } from 'lib/so5';

import { FixtureOverview } from './FixtureOverview';
import { UpcomingFixtureOverview } from './UpcomingFixtureOverview';
import { FixtureTeamsOverview_so5Fixture } from './__generated__/index.graphql';

const DropdownContent = styled(Vertical)`
  border-radius: var(--double-unit) var(--double-unit) 0 0;
  padding: var(--double-unit);
  ${regularMaterial}

  @media ${laptopAndAbove} {
    width: min-content;
    border-radius: var(--double-unit);
    max-height: calc(
      var(--radix-popover-content-available-height, 100vh) - var(--double-unit)
    );
    overflow: auto;
    padding: calc(6 * var(--unit)) var(--double-unit) var(--double-unit);

    &.transparent {
      border: 1px solid var(--c-nd-150);
    }
  }
`;

const TabBarContainer = styled(Horizontal)`
  margin-bottom: var(--double-unit);
`;
const scrollToContender = (contenderSlug: string) => {
  const firstContenderNode = document.getElementById(contenderSlug);
  if (firstContenderNode) {
    flushSync(() => {
      firstContenderNode.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    });
  }
};

const computeSo5FixtureData = (so5Fixture: FixtureTeamsOverview_so5Fixture) => {
  const isUpcoming = isFixtureOpened(so5Fixture);

  const so5LeaderboardGroupInterfacesWithContendersNotTraining =
    so5Fixture.so5LeaderboardGroups.filter(
      so5LeaderboardGroup =>
        so5LeaderboardGroup.mySo5LeaderboardContenders.filter(
          contender =>
            isUpcoming ||
            contender.so5Leaderboard.so5TournamentType.so5LeaderboardType !==
              So5LeaderboardType.SPECIAL_TRAINING_CENTER
        ).length
    );

  const { total, composedOrRewarded } =
    so5LeaderboardGroupInterfacesWithContendersNotTraining
      .flatMap(league => league.mySo5LeaderboardContenders)
      .reduce(
        (acc, contender) => {
          const composed = contender?.so5Lineup?.confirmable ? 1 : 0;
          const rewarded =
            contender?.so5Lineup?.so5Rankings.at(0)?.eligibleOrSo5Rewards
              .length && hasGamesStarted(contender.so5Lineup)
              ? 1
              : 0;

          return {
            composedOrRewarded:
              acc.composedOrRewarded + (isUpcoming ? composed : rewarded),
            total: acc.total + 1,
          };
        },
        { composedOrRewarded: 0, total: 0 }
      );

  const icon = isUpcoming ? (
    <FontAwesomeIcon
      icon={faCheckCircle}
      color={
        composedOrRewarded === total ? 'var(--c-rivals-win)' : 'var(--c-nd-600)'
      }
    />
  ) : (
    <FontAwesomeIcon
      icon={faGift}
      color={composedOrRewarded ? 'var(--c-rivals-win)' : 'var(--c-nd-600)'}
    />
  );

  if (!total) {
    return null;
  }

  return { isUpcoming, total, composedOrRewarded, icon };
};

type Props = {
  so5Fixtures: FixtureTeamsOverview_so5Fixture[];
  trapClickOutside?: boolean;
  transparent?: boolean;
};

export const FixtureTeamsOverview = ({
  so5Fixtures,
  trapClickOutside,
  transparent = true,
}: Props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const track = useEvents();

  const [selectedFixtureIndex, setSelectedFixtureIndex] = useState(0);

  const computedData = so5Fixtures.map(computeSo5FixtureData);

  const validFixtures = so5Fixtures.filter((_f, i) => computedData[i]);

  const selectedFixture = validFixtures.at(selectedFixtureIndex);

  const cumulatedData = computedData.reduce(
    (acc, curr) => {
      return !curr
        ? acc
        : {
            isUpcoming: acc.isUpcoming || curr?.isUpcoming,
            icon: acc.icon || curr?.icon,
            total: acc.total + (curr?.total || 0),
            composedOrRewarded:
              acc.composedOrRewarded + (curr?.composedOrRewarded || 0),
          };
    },
    {
      isUpcoming: false,
      icon: undefined,
      total: 0,
      composedOrRewarded: 0,
    } as {
      isUpcoming: boolean;
      icon?: ReactNode;
      total: number;
      composedOrRewarded: number;
    }
  );

  const goToContender = (value: string) => {
    if (!selectedFixture) {
      return;
    }

    const path = `${generateSportPath(ANY_SPORT_PLAY_EVENTTYPE_FIXTURE, {
      params: {
        fixture: selectedFixture.slug,
        eventType: selectedFixture.type.toLowerCase(),
      },
      sport: selectedFixture.sport,
    })}`;

    if (matchPath(path, location.pathname || '')) {
      scrollToContender(value);
    } else {
      navigate(`${path}#${value}`);
    }
  };

  return (
    <Dropdown
      transparent={transparent}
      gap={8}
      trapClickOutside={trapClickOutside}
      onOpen={() => {
        if (selectedFixture) {
          track('Open Fixture Overview', {
            so5FixtureSlug: selectedFixture.slug,
            so5FixtureState: selectedFixture.aasmState,
          });
        }
      }}
      label={({ open, ...props }) => (
        <Button
          color="tertiary"
          size="medium"
          className={classNames({ active: open })}
          {...props}
        >
          <Horizontal as="span">
            {cumulatedData?.icon}
            {cumulatedData?.composedOrRewarded}
            {' / '}
            {cumulatedData?.total}
          </Horizontal>
        </Button>
      )}
    >
      {({ closeDropdown }) => (
        <DropdownContent className={classNames({ transparent })}>
          {validFixtures.length > 1 && (
            <TabBarContainer center>
              <TabBar variant="primary" value={selectedFixtureIndex.toString()}>
                {validFixtures.map((fixture, index) => (
                  <Tab
                    key={fixture.slug}
                    onClick={() => setSelectedFixtureIndex(index)}
                    value={index.toString()}
                  >
                    <Horizontal>
                      <FormattedMessage
                        {...(fixture.type === So5FixtureEvent.DAILY
                          ? glossary.daily
                          : glossary.biweekly)}
                      />

                      <Horizontal as="span">
                        {computedData[index]?.icon}
                        {computedData[index]?.composedOrRewarded}
                        {' / '}
                        {computedData[index]?.total}
                      </Horizontal>
                    </Horizontal>
                  </Tab>
                ))}
              </TabBar>
            </TabBarContainer>
          )}
          {computedData[selectedFixtureIndex]?.isUpcoming && selectedFixture ? (
            <UpcomingFixtureOverview
              so5Fixture={selectedFixture}
              onClose={closeDropdown}
              scrollToContender={goToContender}
            />
          ) : (
            <FixtureOverview
              so5Fixture={selectedFixture}
              onClose={closeDropdown}
              scrollToContender={goToContender}
            />
          )}
        </DropdownContent>
      )}
    </Dropdown>
  );
};

FixtureTeamsOverview.fragments = {
  so5Fixture: gql`
    fragment FixtureTeamsOverview_so5Fixture on So5Fixture {
      slug
      aasmState
      type
      sport
      so5LeaderboardGroups {
        slug
        mySo5LeaderboardContenders {
          slug
          so5Leaderboard {
            slug
            so5TournamentType {
              id
              so5LeaderboardType
            }
          }
          so5Lineup {
            id
            confirmable
            so5Rankings {
              id
              eligibleOrSo5Rewards {
                ... on So5Reward {
                  id
                  slug
                }
              }
            }
            ...hasGamesStarted_so5Lineup
          }
        }
      }
      ...UpcomingFixtureOverview_so5Fixture
      ...FixtureOverview_so5Fixture
    }
    ${UpcomingFixtureOverview.fragments.so5Fixture}
    ${FixtureOverview.fragments.so5Fixture}
    ${hasGamesStarted.fragments.so5Lineup}
  ` as TypedDocumentNode<FixtureTeamsOverview_so5Fixture>,
};
